'use client';

import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import { Sidebar } from '@/components/Sidebar';
import { UserMenu } from '@/components/UserMenu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import Link from 'next/link';
import { Logo } from '@/components/Logo';

// Routes that should NOT show the dashboard layout
const EXCLUDED_PREFIXES = ['/auth', '/preview'];

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, loading, currentOrg } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Don't show dashboard layout for excluded routes or when not authenticated
  const isExcluded = EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p));

  if (isExcluded || loading || !user) {
    return <>{children}</>;
  }

  // User has no memberships and is not a superadmin — show minimal chrome
  // so they can at least sign out from the top bar.
  const hasNoAccess = !currentOrg && !user.is_superadmin;
  if (hasNoAccess) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <header className="border-b">
          <div className="container mx-auto px-6 h-14 flex items-center justify-between max-w-7xl">
            <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Logo className="h-6 w-auto" />
            </Link>
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="container mx-auto px-6 py-6 max-w-7xl">
            {children}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed top-3 left-3 z-50 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto px-6 py-6 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
}
